import { useFormik } from 'formik';
import CustomQuerySelect from '~/components/Shared/CustomQuerySelect/CustomQuerySelect';
import { useDebounce } from '@uidotdev/usehooks';
import CustomSelect from '~/components/Shared/CustomSelect/CustomSelect';
import { useToastError } from '~/utils/useToastError';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { IUsersFilters } from '~/utils/types/userFilterTypes';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { validate } from '~/utils/validateEmployeesFilter';
import styles from './EmployeesFilter.module.scss';
import { FilterButtons } from '../FiltersButtons/FilterButtons';
import { useEffect, useState } from 'react';
import { FilterErrorMessage } from '../FilterErrorMessage/FilterErrorMessage';
import { InputBar } from '~/ui';
import { getCompaniesModuleOptions } from '~/utils/getCompaniesModuleOptions';
import { CustomDatePicker } from '~/components/Shared/CustomDatePicker/CustomDatePicker';

interface IEmployeProps {
  isUsersLoading: boolean;
  selectedUsers: number[];
  onClick: (filters: IUsersFilters) => void;
  setIsStatusUsersModalOpen: () => void;
}

interface IAccomondation {
  id: number;
  name: string;
  full_name?: string;
}

interface IOrganization {
  id: number;
  name: string;
}

interface IFormik {
  accommodation_ids: IAccomondation[];
  announcement_organizations: IOrganization[];
  dates: {
    start_date: Dayjs | null;
    end_date: Dayjs | null;
  };
  login_dates: {
    start_date: Dayjs | null;
    end_date: Dayjs | null;
  };
  user: {
    status: { id: number; name: string; display_name: string };
    type: {
      type: string;
      id: number;
      name: string;
      display_name?: string;
      full_name?: string;
    }[];
  };
  login_count: string;
  login_condition: { id: number; name: string; display_name: string };
}

export const EmployeesFilter = ({
  isUsersLoading,
  onClick,
  selectedUsers,
  setIsStatusUsersModalOpen,
}: IEmployeProps) => {
  const [isAnyFieldFilled, setIsAnyFieldFilled] = useState(false);
  const toastError = useToastError();
  const debouncedSearchTerm = useDebounce('', 500);

  const { data } = useQuery({
    queryFn: async () => {
      const response = await instance.get('admin/users-grade');
      return response.data;
    },
    queryKey: ['grades'],
    onError: error => {
      toastError(error);
    },
  });

  const { data: companies } = useQuery({
    queryFn: async () => {
      const response = await instance.get('admin/user-company');
      return response.data;
    },
    queryKey: ['user-company'],
    onError: error => {
      toastError(error);
    },
  });

  const companyModules = getCompaniesModuleOptions(companies?.companies);

  const grades = data?.grade;

  const formik = useFormik<IFormik>({
    initialValues: {
      accommodation_ids: [],
      announcement_organizations: [],
      dates: {
        start_date: null,
        end_date: null,
      },
      login_dates: {
        start_date: null,
        end_date: null,
      },
      user: {
        status: { id: 1, name: '2', display_name: `${t('all_user')}` },
        type: [],
      },
      login_count: '',
      login_condition: { id: 0, name: '', display_name: '' },
    },
    validate,
    onSubmit: values => {
      const accommodation_ids = values.accommodation_ids.map(item => item.id);
      const company_name = values.announcement_organizations.map(
        item => item.name
      );
      const grade_type = values.user.type.map(item => item.type);
      const filter = {
        ...((values.dates.start_date && values.dates.end_date) ||
        values.accommodation_ids
          ? {
              business_trips: {
                ...(values.accommodation_ids.length !== 0 && {
                  accommodation_ids: accommodation_ids,
                }),
                ...(values.dates.end_date &&
                  values.dates.start_date && {
                    dates: {
                      start_date: dayjs(values.dates.start_date)
                        .startOf('day')
                        .format('YYYY-MM-DD HH:mm:ss'),
                      end_date: dayjs(values.dates.end_date)
                        .endOf('day')
                        .format('YYYY-MM-DD HH:mm:ss'),
                    },
                  }),
              },
            }
          : {}),
        ...(values.announcement_organizations.length !== 0
          ? {
              companies: {
                names: company_name,
              },
            }
          : {}),
        users: {
          ...((values.user.status.name === '1' ||
            values.user.status.name === '0') && {
            is_blocked: Number(values.user.status.name),
          }),
          ...(values.user.type.length !== 0 && {
            grade_type: grade_type,
          }),
        },
        ...(values.login_count && values.login_condition
          ? {
              login: {
                count: Number(values.login_count),
                condition: values.login_condition.name,
                ...(values.login_dates.start_date && values.login_dates.end_date
                  ? {
                      dates: {
                        start_date: dayjs(values.login_dates.start_date)
                          .startOf('day')
                          .format('YYYY-MM-DD HH:mm:ss'),
                        end_date: dayjs(values.login_dates.end_date)
                          .endOf('day')
                          .format('YYYY-MM-DD HH:mm:ss'),
                      },
                    }
                  : {}),
              },
            }
          : {}),
      };
      onClick(filter);
    },
  });

  const handleResetForm = () => {
    formik.resetForm();
    onClick({});
  };

  useEffect(() => {
    const isFilled =
      !!formik.values.dates.start_date ||
      !!formik.values.dates.end_date ||
      !!formik.values.accommodation_ids.length ||
      !!formik.values.announcement_organizations.length ||
      !!formik.values.user.type.length ||
      formik.values.user.status.name !== '2';
    setIsAnyFieldFilled(isFilled);
  }, [formik.values]);

  return (
    <div className={styles.employersContainer}>
      <form onSubmit={formik.handleSubmit}>
        <div className={styles.formWrapper}>
          <div className={styles.placeWrapper}>
            <div className={styles.palce}>
              <CustomQuerySelect
                label={t('accommodation_facility')}
                onChange={value => {
                  formik.setFieldValue('accommodation_ids', value);
                }}
                value={formik.values.accommodation_ids}
                query="admin/accommodations"
                placeholder={t('accommodations_validate')}
                isSearchEnabled
                queryKey={['announcement_accommodations', debouncedSearchTerm]}
                params={{
                  sort: '',
                  perPage: 100,
                  field: '',
                  page: 1,
                }}
                multiselect
              />
            </div>
            <div className={styles.palce}>
              <CustomSelect
                label={t('company')}
                options={companyModules}
                value={formik.values.announcement_organizations}
                onChange={value => {
                  formik.setFieldValue('announcement_organizations', value);
                }}
                placeholder={t('validate_organizations')}
                disabled={isUsersLoading}
                multiselect
              />
            </div>
          </div>
          <div className={styles.dateWrapper}>
            <CustomDatePicker
              title={t('start_n_end_business_trip_date')}
              placeholder={[t('date'), t('date')]}
              value={[
                formik.values.dates.start_date,
                formik.values.dates.end_date,
              ]}
              onChange={(name, value) => {
                if (name === 'start_date') {
                  formik.setFieldValue('dates.start_date', value);
                } else {
                  formik.setFieldValue('dates.end_date', value);
                }
              }}
              onBlur={() => {
                formik.setFieldTouched('dates.start_date', true);
                formik.setFieldTouched('dates.end_date', true);
              }}
            />
            {formik.errors.dates && (
              <FilterErrorMessage message={formik.errors.dates as string} />
            )}
          </div>
          <div className={styles.typeWrapper}>
            <CustomSelect
              label={t('user_type')}
              options={grades}
              value={formik.values.user.type}
              onChange={value => {
                formik.setFieldValue('user.type', value);
              }}
              placeholder={t('validate_user_type')}
              disabled={isUsersLoading}
              multiselect
            />
            <CustomSelect
              label={t('user_status')}
              options={[
                { id: 1, name: '2', display_name: `${t('all_user')}` },
                { id: 2, name: '0', display_name: `${t('active_user')}` },
                { id: 3, name: '1', display_name: `${t('blocked_user')}` },
              ]}
              value={formik.values.user.status}
              onChange={value => {
                formik.setFieldValue('user.status', value);
              }}
              placeholder={t('validate_user_status')}
              disabled={isUsersLoading}
            />
          </div>
          <div className={styles.countWrapper}>
            <CustomSelect
              label={t('condition_login')}
              value={formik.values.login_condition}
              options={[
                { id: 1, display_name: `${t('more')}`, name: '>' },
                { id: 2, display_name: `${t('less')}`, name: '<' },
                { id: 3, display_name: `${t('equal')}`, name: '=' },
              ]}
              placeholder={t('select_operator')}
              onChange={value => formik.setFieldValue('login_condition', value)}
              className={styles.conditionSelect}
              error={formik.errors.login_condition?.name}
            />
            <InputBar
              label={t('entrance_quantity')}
              placeholder={t('add_entrance_quantity')}
              name="login_count"
              value={formik.values.login_count}
              onChange={formik.handleChange}
              classNameInputWrapper={styles.countInput}
              errors={formik.errors.login_count}
              onBlur={formik.handleBlur}
              disabled={formik.values.login_condition.id === 0}
              error={
                formik.touched.login_count && Boolean(formik.errors.login_count)
              }
            />
          </div>
          <div className={styles.dateWrapper}>
            <CustomDatePicker
              title={t('login_period')}
              placeholder={[t('date'), t('date')]}
              value={[
                formik.values.login_dates.start_date,
                formik.values.login_dates.end_date,
              ]}
              onChange={(name, value) => {
                if (name === 'start_date') {
                  formik.setFieldValue('login_dates.start_date', value);
                } else {
                  formik.setFieldValue('login_dates.end_date', value);
                }
              }}
              onBlur={() => {
                formik.setFieldTouched('login_dates.start_date', true);
                formik.setFieldTouched('login_dates.end_date', true);
              }}
            />
          </div>
        </div>
        <FilterButtons
          setIsModalOpen={setIsStatusUsersModalOpen}
          isAnyFieldFilled={isAnyFieldFilled}
          selectedUsers={selectedUsers}
          resetForm={handleResetForm}
          isLoading={isUsersLoading}
        />
      </form>
    </div>
  );
};
